import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Megaphone,
  Plus,
  Search,
  Calendar,
  Trash2,
  Pin,
  PinOff,
  Send,
  Bell,
  UserRound,
  Users,
  X,
} from "lucide-react";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const categoryStyle = {
  general: "bg-slate-100 text-slate-700 dark:bg-slate-800/80 dark:text-slate-200",
  important: "bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-300",
  event: "bg-[var(--accent-light)] text-[var(--accent-primary)] dark:bg-[var(--accent-subtle)] dark:text-[var(--heading-secondary)]",
  holiday: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-300",
};

const formatAnnouncementDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getAuthorName = (announcement) => {
  if (announcement.author?.name) return announcement.author.name;
  if (announcement.postedByName) return announcement.postedByName;
  return "Admin";
};

const getRecipientNames = (announcement) => {
  const recipients = Array.isArray(announcement.recipients)
    ? announcement.recipients
    : [];

  return recipients
    .map((employee) => {
      if (employee && typeof employee === "object") {
        return [employee.name, employee.employeeId ? `(${employee.employeeId})` : ""]
          .filter(Boolean)
          .join(" ");
      }
      return "";
    })
    .filter(Boolean);
};

const getAudienceLabel = (announcement) => {
  const names = getRecipientNames(announcement);
  if (names.length) return names.join(", ");
  if (announcement.visibleTo === "team") return "All teammates";
  if (announcement.visibleTo === "selected") return "Selected employees";
  return "All employees";
};

const AdminAnnouncements = () => {

  const [announcements, setAnnouncements] =
    useState([]);

  const [, setLoading] =
    useState(true);

  const [search, setSearch] =
    useState("");

  const [creating, setCreating] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [pinningId, setPinningId] =
    useState(null);

  const [form, setForm] =
    useState({
      title: "",
      body: "",
      category: "general",
      visibleTo: "all",
      isPinned: false,
      expiresAt: "",
    });

  // ================= FETCH =================
  const fetchAnnouncements = async () => {

    try {

      const res = await api.get(
        "/api/admin/announcements",
      );

      setAnnouncements(
        res.data || [],
      );

    } catch (err) {

      console.error(err);

    } finally {

      setLoading(false);
    }
  };

  useEffect(() => {
        document.title = "Announcements | Savrion Technologies";
    fetchAnnouncements();
  }, []);

  // ================= CREATE =================
  const handleCreate = async (e) => {

    e.preventDefault();

    if (
      !form.title.trim() ||
      !form.body.trim()
    ) {
      return;
    }

    try {

      setSubmitting(true);

      const res = await api.post(
        "/api/admin/announcements",
        form,
      );
      setAnnouncements((current) => [res.data, ...current]);

      setForm({
        title: "",
        body: "",
        category: "general",
        visibleTo: "all",
        isPinned: false,
        expiresAt: "",
      });

      setCreating(false);

      window.setTimeout(() => fetchAnnouncements(), 0);

    } catch (err) {

      console.error(err);

    } finally {

      setSubmitting(false);
    }
  };

  // ================= PIN / UNPIN =================
  const handleTogglePin = async (announcement) => {

    const nextPinned = !announcement.isPinned;
    const previousAnnouncements = announcements;

    setPinningId(announcement._id);
    setAnnouncements((current) =>
      current.map((item) =>
        item._id === announcement._id
          ? { ...item, isPinned: nextPinned }
          : item,
      ),
    );

    try {

      const res = await api.patch(
        `/api/admin/announcements/${announcement._id}/pin`,
        { isPinned: nextPinned },
      );

      setAnnouncements((current) =>
        current.map((item) =>
          item._id === announcement._id
            ? res.data
            : item,
        ),
      );

    } catch (err) {

      setAnnouncements(previousAnnouncements);
      console.error(err);

    } finally {

      setPinningId(null);
    }
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {

    const ok = window.confirm(
      "Delete this announcement?",
    );

    if (!ok) return;

    const deletedAnnouncement = announcements.find((announcement) => announcement._id === id);
    setAnnouncements((prev) =>
      prev.filter(
        (a) => a._id !== id,
      ),
    );

    try {

      await api.delete(
        `/api/admin/announcements/${id}`,
      );

    } catch (err) {

      if (deletedAnnouncement) {
        setAnnouncements((prev) => [deletedAnnouncement, ...prev]);
      }
      console.error(err);
    }
  };

  // ================= FILTER =================
  const filteredAnnouncements = useMemo(() => {

    return announcements.filter((a) => {

      const q = search.toLowerCase();

      return (
        a.title
          ?.toLowerCase()
          .includes(q) ||
        a.body
          ?.toLowerCase()
          .includes(q) ||
        getAuthorName(a)
          .toLowerCase()
          .includes(q) ||
        getAudienceLabel(a)
          .toLowerCase()
          .includes(q)
      );
    }).sort((a, b) => {
      if (Boolean(a.isPinned) !== Boolean(b.isPinned)) {
        return a.isPinned ? -1 : 1;
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });

  }, [announcements, search]);

  // ================= STATS =================
  const stats = useMemo(() => {

    const total = announcements.length;

    const pinned = announcements.filter(
      (a) => a.isPinned,
    ).length;

    return {
      total,
      pinned,
    };

  }, [announcements]);

  return (
    <div
      className="
            flex flex-col xl:flex-row
            min-h-screen

            bg-[var(--bg-primary)]
            text-[var(--text-primary)]
        "
    >

      <AdminNavbar />

      <div
        className="
                flex-1
                min-w-0

                px-4
                md:px-6

                py-6

                space-y-6
            "
      >

        {/* ================= HEADER ================= */}
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[var(--accent-primary)]">
                <Bell size={15} />
                Admin Broadcast Center
              </div>
              <h1 className="text-2xl font-black md:text-3xl">
                Announcements
              </h1>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Manage company updates, notices and internal communication.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { title: "Total", value: stats.total, icon: <Megaphone size={15} /> },
                  { title: "Pinned", value: stats.pinned, icon: <Pin size={15} /> },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="min-w-[86px] rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2"
                  >
                    <div className="mb-1 flex items-center gap-1.5 text-[10px] font-black uppercase text-[var(--text-secondary)]">
                      {item.icon}
                      {item.title}
                    </div>
                    <p className="text-lg font-black">{item.value}</p>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setCreating(true)}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--accent-primary)] px-4 text-sm font-black text-white transition-all hover:bg-[var(--accent-hover)]"
              >
                <Plus size={18} />
                Create
              </button>
            </div>
          </div>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="flex flex-col gap-3 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3 md:flex-row md:items-center md:justify-between">
          <div className="flex h-11 min-w-0 flex-1 items-center gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4">
            <Search size={18} className="shrink-0 text-[var(--text-secondary)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search announcements..."
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
          </div>

          <p className="px-1 text-xs font-bold text-[var(--text-secondary)]">
            Showing {filteredAnnouncements.length} of {announcements.length}
          </p>
        </div>

        {/* ================= LIST ================= */}
        <div className="min-h-0">

          {filteredAnnouncements.length ===
            0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center">
              <Megaphone size={42} className="mx-auto mb-4 text-[var(--text-secondary)]" />

              <h3
                className="
                                text-xl
                                font-bold

                                mb-2
                            "
              >
                No announcements found
              </h3>

              <p
                className="
                                text-[var(--text-secondary)]
                            "
              >
                Create your first announcement.
              </p>

            </div>
          ) : (
            <div
              className="grid gap-3"
              style={{
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 360px))",
                justifyContent: "start",
              }}
            >
              {filteredAnnouncements.map((announcement) => (
                <article
                  key={announcement._id}
                  className="group rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-1.5">
                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase ${categoryStyle[announcement.category] || categoryStyle.general}`}>
                          {announcement.category || "general"}
                        </span>

                        {announcement.isPinned && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-black uppercase text-amber-600 dark:bg-amber-950/30 dark:text-amber-300">
                            <Pin size={11} />
                            Pinned
                          </span>
                        )}

                        <span className="rounded-full bg-[var(--bg-primary)] px-2 py-0.5 text-[9px] font-black uppercase text-[var(--text-secondary)]">
                          {announcement.visibleTo || "all"}
                        </span>
                      </div>

                      <h2 className="break-words text-base font-black leading-snug">
                        {announcement.title}
                      </h2>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleTogglePin(announcement)}
                        disabled={pinningId === announcement._id}
                        className={`flex h-9 w-9 items-center justify-center rounded-xl transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                          announcement.isPinned
                            ? "bg-amber-500/10 text-amber-600 hover:bg-amber-500 hover:text-white dark:text-amber-300"
                            : "bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-amber-500 hover:text-white"
                        }`}
                        title={announcement.isPinned ? "Unpin announcement" : "Pin announcement"}
                        aria-label={announcement.isPinned ? "Unpin announcement" : "Pin announcement"}
                      >
                        {announcement.isPinned ? <PinOff size={16} /> : <Pin size={16} />}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(announcement._id)}
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-500/10 text-red-500 transition-all hover:bg-red-500 hover:text-white"
                        title="Delete announcement"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <p
                    className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-[var(--text-secondary)]"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {announcement.body}
                  </p>

                  <div className="mt-3 grid gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-2.5 text-[11px] font-bold text-[var(--text-secondary)]">
                    <div className="flex min-w-0 items-center gap-2">
                      <UserRound size={13} className="shrink-0 text-[var(--accent-primary)]" />
                      <span className="shrink-0 uppercase">By</span>
                      <strong className="min-w-0 truncate text-[var(--text-primary)]">
                        {getAuthorName(announcement)}
                      </strong>
                    </div>
                    <div className="flex min-w-0 items-start gap-2">
                      <Users size={13} className="mt-0.5 shrink-0 text-[var(--accent-primary)]" />
                      <span className="shrink-0 uppercase">Assigned to</span>
                      <strong
                        className="min-w-0 break-words text-[var(--text-primary)]"
                        title={getAudienceLabel(announcement)}
                      >
                        {getAudienceLabel(announcement)}
                      </strong>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-[var(--border-color)] pt-2 text-[11px] font-bold text-[var(--text-secondary)]">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-primary)] px-2.5 py-1">
                      <Calendar size={13} />
                      {formatAnnouncementDate(announcement.createdAt)}
                    </span>

                    {announcement.expiresAt && (
                      <span className="rounded-full bg-[var(--bg-primary)] px-2.5 py-1">
                        Expires {formatAnnouncementDate(announcement.expiresAt)}
                      </span>
                    )}
                  </div>

                </article>
              ))}
            </div>
          )}

        </div>

      </div>

      {/* ================= CREATE MODAL ================= */}
      {creating && (
        <div
          className="
                    fixed
                    inset-0

                    z-[999]

                    bg-black/70
                    backdrop-blur-sm

                    flex
                    items-center
                    justify-center

                    p-4
                "
        >

          <div
            className="
                        w-full
                        max-w-2xl

                        rounded-3xl

                        border
                        border-[var(--border-color)]

                        bg-[var(--card-bg)]

                        p-7
                    "
          >

            <div className="mb-6 flex items-start justify-between gap-4">
              <div>

              <h2
                className="
                                text-3xl
                                font-bold

                                mb-2
                            "
              >
                Create Announcement
              </h2>

              <p
                className="
                                text-[var(--text-secondary)]
                            "
              >
                Broadcast important updates.
              </p>
              </div>
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--border-color)] hover:bg-[var(--bg-primary)]"
                aria-label="Close announcement form"
              >
                <X size={18} />
              </button>

            </div>

            <form
              onSubmit={handleCreate}
              className="space-y-5"
            >

              <input
                type="text"
                placeholder="Announcement title"
                value={form.title}
                onChange={(e) =>
                  setForm({
                    ...form,
                    title:
                      e.target.value,
                  })
                }
                className="
                                w-full

                                rounded-2xl

                                border
                                border-[var(--border-color)]

                                bg-[var(--bg-primary)]

                                px-5
                                py-4
                            "
              />

              <textarea
                rows={6}
                placeholder="Write announcement details..."
                value={form.body}
                onChange={(e) =>
                  setForm({
                    ...form,
                    body:
                      e.target.value,
                  })
                }
                className="
                                w-full

                                rounded-2xl

                                border
                                border-[var(--border-color)]

                                bg-[var(--bg-primary)]

                                px-5
                                py-4
                            "
              />

              <div
                className="
                                grid
                                grid-cols-1
                                md:grid-cols-2

                                gap-4
                            "
              >

                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      category:
                        e.target.value,
                    })
                  }
                  className="
                                    rounded-2xl

                                    border
                                    border-[var(--border-color)]

                                    bg-[var(--bg-primary)]

                                    px-5
                                    py-4
                                "
                >
                  <option value="general">
                    General
                  </option>

                  <option value="important">
                    Important
                  </option>

                  <option value="event">
                    Event
                  </option>

                  <option value="holiday">
                    Holiday
                  </option>
                </select>

                <input
                  type="date"
                  value={form.expiresAt}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      expiresAt:
                        e.target.value,
                    })
                  }
                  className="
                                    rounded-2xl

                                    border
                                    border-[var(--border-color)]

                                    bg-[var(--bg-primary)]

                                    px-5
                                    py-4
                                "
                />

              </div>

              <label
                className="
                                flex
                                items-center

                                gap-3

                                text-sm
                            "
              >

                <input
                  type="checkbox"
                  checked={form.isPinned}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      isPinned:
                        e.target.checked,
                    })
                  }
                />

                Pin this announcement

              </label>

              <div
                className="
                                flex
                                items-center
                                justify-end

                                gap-3

                                pt-3
                            "
              >

                <button
                  type="button"
                  onClick={() =>
                    setCreating(false)
                  }
                  className="
                                    px-5
                                    py-3

                                    rounded-2xl

                                    border
                                    border-[var(--border-color)]
                                "
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  className="
                                    flex
                                    items-center
                                    justify-center

                                    gap-2

                                    px-6
                                    py-3

                                    rounded-2xl

                                    bg-[var(--accent-primary)]
                                    hover:bg-[var(--accent-hover)]

                                    text-white
                                    font-semibold
                                "
                >
                  <Send size={18} />
                  {submitting
                    ? "Publishing..."
                    : "Publish"}
                </button>

              </div>

            </form>

          </div>

        </div>
      )}

    </div>
  );
};

export default AdminAnnouncements;



